import { useCallback, useEffect, useState } from "react";
import { PiGraphDuotone } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import { repositorioCategoriaMestra } from "../../sync/repositories/financeiro";

const AJUDA = {
  titulo: "Cadastro de Categorias Mestra",
  descricao:
    "Categorias Mestra são o agrupamento mais amplo das finanças da fazenda (ex.: 'Insumos Agrícolas', 'Despesas Administrativas'). Cada Categoria Financeira criada depois precisa estar vinculada a uma Categoria Mestra — pense nela como a 'pasta' que organiza as categorias menores.",
  secoes: [
    {
      titulo: "Dados da Categoria Mestra",
      campos: [
        {
          nome: "Nome da Categoria Mestra",
          obrigatorio: true,
          explicacao: "Nome do grupo principal de classificação financeira. Use nomes amplos — o detalhamento fica para as Categorias Financeiras vinculadas a ela.",
        },
      ],
    },
  ],
  dicas: [
    "Uma categoria mestra com subcategorias (Categorias Financeiras) vinculadas não pode ser excluída — o botão de excluir fica desabilitado nesse caso.",
  ],
};

export default function GestaoCategoriasMestra() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [categorias, setCategorias] = useState([]);
  const [categoriasFiltradas, setCategoriasFiltradas] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  
  const [filtros, setFiltros] = useState({
    busca: ""
  });
  
  const [formData, setFormData] = useState({
    nome: "",
  });

  // Carregar categorias mestra
  const carregarCategorias = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioCategoriaMestra.listar();
      const dados = response.data.results || response.data;
      setCategorias(Array.isArray(dados) ? dados : []);
      setCategoriasFiltradas(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar categorias mestra:", error);
      alert("Erro ao carregar lista de categorias mestra");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarCategorias();
  }, [carregarCategorias]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validarFormulario = () => {
    if (!formData.nome.trim()) {
      alert("Preencha o nome da categoria mestra.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) return;
    
    setLoading(true);

    const payload = {
      nome: formData.nome,
    };

    try {
      if (editando) {
        await repositorioCategoriaMestra.atualizar(editandoId, payload);
        alert("Categoria mestra atualizada com sucesso!");
        handleCancel();
        carregarCategorias();
      } else {
        await repositorioCategoriaMestra.criar(payload);
        alert("Categoria mestra cadastrada com sucesso!");
        handleCancel();
        carregarCategorias();
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (categoria) => {
    setFormData({
      nome: categoria.nome || "",
    });
    setEditando(true);
    setEditandoId(categoria.id);
    setMostrarFormulario(true);
    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovaCategoria = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      nome: "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta categoria mestra? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await repositorioCategoriaMestra.excluir(id);
      alert("Categoria mestra excluída com sucesso!");
      carregarCategorias();
      
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir categoria mestra. Verifique se não há registros vinculados.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = Array.isArray(categorias) ? [...categorias] : [];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(c => 
        c.nome?.toLowerCase().includes(buscaLower) ||
        c.id?.toString().includes(buscaLower)
      );
    }
    
    setCategoriasFiltradas(filtrados);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "" });
    setCategoriasFiltradas(Array.isArray(categorias) ? categorias : []);
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Categorias Mestra <PiGraphDuotone size={38} /></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovaCategoria}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nova Categoria Mestra
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Categoria Mestra" : "Cadastrar Nova Categoria Mestra"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Código</label>
                    <input
                      type="text"
                      className="border border-gray-300 p-2 rounded bg-gray-100 text-gray-600"
                      value={editando ? editandoId : "Automático"}
                      readOnly
                    />
                    <span className="text-xs text-gray-500 mt-1">
                      Gerado automaticamente pelo sistema
                    </span>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Nome da Categoria Mestra *</label>
                    <input
                      type="text"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="Ex: Insumos Agrícolas, Medicamentos, Equipamentos, etc."
                      required
                    />
                    <span className="text-xs text-gray-500 mt-1">
                      Nome da categoria principal para classificação financeira
                    </span>
                  </div>
                </div>

                {/* Botões do formulário */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Código ou Nome</label>
                  <input
                    type="text"
                    placeholder="Digite o código ou nome da categoria..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Categorias Mestra */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-600">
                  {categoriasFiltradas.length} categoria(s) mestra(s) encontrada(s)
                </p>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Nome</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Subcategorias</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoriasFiltradas.map((categoria) => (
                        <tr key={categoria.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                          <td className="p-3 text-sm font-mono">{categoria.id}</td>
                          <td className="p-3 text-sm font-medium">{categoria.nome}</td>
                          <td className="p-3 text-sm font-medium">{categoria.subcategorias_count || 0} <span className="text-gray-500 text-xs ml-1">subcategorias</span></td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditar(categoria)}
                                className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Editar"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleExcluir(categoria.id)}
                                className={`px-3 py-1 rounded text-sm transition-colors ${
                                  categoria.subcategorias_count > 0
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-red-600 hover:bg-red-700 text-white"
                                }`}
                                title={categoria.subcategorias_count > 0 ? "Categoria com subcategorias não pode ser excluída" : "Excluir"}
                                disabled={categoria.subcategorias_count > 0}
                              >
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      
                      {categoriasFiltradas.length === 0 && (
                        <tr>
                          <td colSpan="4" className="p-8 text-center text-gray-500">
                            {filtros.busca 
                              ? "Nenhuma categoria mestra encontrada com os filtros selecionados."
                              : "Nenhuma categoria mestra cadastrada."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}